import { motion } from "framer-motion";
import { Star, ShieldCheck, Sparkles } from "lucide-react";
import { TESTIMONIALS } from "@/data/content";

/**
 * ReviewsHero - high-trust mini-reviews block placed just below the hero
 * showcase. Bright 5-star ratings, social proof count, and 3 concise review
 * cards. Designed to convert "this looks pretty" into "this is real and
 * trusted" in under 4 seconds.
 */
function StarRow({ count = 5 }) {
  return (
    <div className="inline-flex gap-0.5" aria-label={`${count} out of 5 stars`}>
      {Array.from({ length: count }).map((_, i) => (
        <Star key={`star-${i}`} size={14} className="fill-current text-amber-400" strokeWidth={0} />
      ))}
    </div>
  );
}

export default function ReviewsHero() {
  // Use first 3 testimonials and craft a shorter pull-quote per card.
  const cards = TESTIMONIALS.slice(0, 3).map((t) => ({
    ...t,
    short: t.quote.split(". ")[0] + ".",
  }));

  return (
    <section
      data-testid="reviews-hero"
      className="relative px-5 sm:px-6 lg:px-12 py-20 sm:py-28 border-y border-neutral-200/70 bg-[#FAFAFA]"
    >
      <div className="max-w-[1400px] mx-auto">
        {/* Header row */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="flex flex-wrap items-center justify-center gap-x-10 gap-y-5 mb-12 sm:mb-16 text-center"
        >
          <div className="flex items-center gap-3" data-testid="reviews-rating">
            <StarRow />
            <span className="text-neutral-900 font-medium text-base">4.9 / 5</span>
            <span className="text-neutral-500 text-sm">from 312 reviews</span>
          </div>
          <span className="hidden sm:block w-px h-6 bg-neutral-100" aria-hidden />
          <div className="flex items-center gap-2 text-neutral-700 text-sm" data-testid="reviews-trust-1">
            <ShieldCheck size={16} className="text-amber-400" />
            Cancel anytime · Export your data
          </div>
          <span className="hidden sm:block w-px h-6 bg-neutral-100" aria-hidden />
          <div className="flex items-center gap-2 text-neutral-700 text-sm" data-testid="reviews-trust-2">
            <Sparkles size={16} className="text-amber-400" />
            Trusted by 3,200+ founders & operators
          </div>
        </motion.div>

        {/* Reviews grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6">
          {cards.map((t, i) => (
            <motion.figure
              key={t.name}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
              data-testid={`review-card-${i}`}
              className="glass rounded-md p-6 sm:p-7 flex flex-col"
            >
              <StarRow />
              <blockquote className="mt-4 text-neutral-900 text-base sm:text-lg leading-relaxed font-medium">
                &ldquo;{t.short}&rdquo;
              </blockquote>
              <figcaption className="mt-5 sm:mt-6 pt-5 sm:pt-6 border-t border-neutral-200 flex items-center gap-3">
                <img
                  src={t.avatar}
                  alt={t.name}
                  loading="lazy"
                  className="w-11 h-11 rounded-full object-cover"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-neutral-900 text-sm font-medium truncate">{t.name}</div>
                  <div className="text-neutral-500 text-xs truncate">{t.role}</div>
                </div>
                {t.sector && (
                  <span className="hidden sm:inline-block text-[10px] uppercase tracking-[0.2em] text-amber-400/90 border border-amber-400/30 px-2 py-1 rounded-sm">
                    {t.sector}
                  </span>
                )}
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
}
